import {computed, inject, Injectable, signal} from '@angular/core';
import {Asset} from 'src/app/common/models/ai-reports/shared/assets';
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';
import {tapResponse} from '@ngrx/operators';
import {BehaviorSubject, EMPTY, filter, firstValueFrom, map, Observable, shareReplay, Subject, switchMap,} from 'rxjs';
import {ChatStore} from 'src/app/services/store';
import {ChatClientService} from 'src/app/services/AIReports/chat-client.service';
import {ClientImageGraphService, ImageGraphEvent} from 'src/app/services/AIReports';
import {AiCreditsService} from 'src/app/services/AIReports/ai-credits.service';
import {DeepAgentStreamState} from 'src/app/common/models/ai-reports/shared/deep-agent-state';
import {Chat} from 'src/app/common/models/ai-reports/shared/chat';
import {AIMessage, BaseMessage} from '@langchain/core/messages';
import {TranslateService} from '@ngx-translate/core';

/**
 * Facade for {@link AiChatComponent}.
 *
 * Owns orchestration/side-effects (stream lifecycle, thread CRUD) so the component
 * can stay focused on view concerns (form controls + template events).
 *
 * Edge cases:
 * - No active project selected -> {@link ClientImageGraphService.chat} throws.
 * - Concurrency: starting a new stream cancels the previous one (latest wins).
 * - Teardown: calling {@link stop} completes the current stream subscription.
 */
@Injectable()
export class AiChatFacade {
	private readonly imageGraphService = inject(ClientImageGraphService);
	private readonly chatStore = inject(ChatStore);
	private readonly chatClient = inject(ChatClientService);
	private readonly creditsService = inject(AiCreditsService);
	private readonly translate = inject(TranslateService);
	/** Re-evaluate translated synthetic messages when the UI language changes. */
	private readonly _langChange = toSignal(this.translate.onLangChange, { initialValue: null });

	/**
	 * Holds the currently active stream observable.
	 * `undefined` means idle.
	 */
	private readonly currentStream$ = new BehaviorSubject<
		Observable<ImageGraphEvent> | undefined
	>(undefined);

	/**
	 * Stream of SSE events for the active request.
	 *
	 * Note: `shareReplay(1)` allows multiple consumers (graphState, init handler)
	 * without re-subscribing to the SSE source.
	 */
	readonly stream$ = this.currentStream$.pipe(
		switchMap((s) => {
			if (!s) return EMPTY;

			// Scoped per-subscription; cancellation (stop()/newThread()/selectThread()) must not trigger a fetch.
			let didCompleteOrError = false;
			return s.pipe(
				tapResponse({
					next: ([node, type, data]) => {
						if (type === 'init') {
							this.chatStore.setThreadId((data as { thread_id: string }).thread_id);
						}
						if (type === 'agent_crashed') {
							// Durable crash state — the backend has already persisted pending_retry
							// on the thread; mirror it locally so the "Try again" bubble shows now.
							this.chatStore.setPendingRetry(true);
						}
					},
					error: (err) => {
						didCompleteOrError = true;
						console.error('[AiChat] Stream error:', err);
					},
					complete: () => { didCompleteOrError = true; },
					finalize: () => {
						this.currentStream$.next(undefined);
						this._lastGeneratedAssetId.set(this.mainAssetId());
						// Refetch on natural end only; guards against a delayed/dropped balance_update SSE push (FT-887).
						if (didCompleteOrError) {
							this.creditsService.fetchBalance();
						}
					},
				})
			);
		}),
		shareReplay(1)
	);

	/**
	 * Latest graph state emitted by the stream.
	 */
	readonly graphState$ = this.stream$.pipe(
		filter((event) => event[1] === 'values' && event[0].length === 0),
		map((event) => event[2] as DeepAgentStreamState)
	);

	/**
	 * True while a stream is active.
	 *
	 * Implemented via {@link toSignal} so templates react to stream start/stop.
	 */
	readonly isStreaming = toSignal(
		this.currentStream$.pipe(map((stream) => !!stream)),
		{ initialValue: false }
	);

	// ── Store re-exports ────────────────────────────────────────────────────────
	readonly activeThreadId = this.chatStore.thread_id;
	readonly history = this.chatStore.history;

	/**
	 * Durable "Something went wrong." bubble, driven by the thread's persisted `pending_retry`
	 * flag (hydrated on thread load, set live on `agent_crashed`, cleared on send/retry). The
	 * `is_agent_crashed` kwarg makes {@link ChatMessageBubbleComponent} render the retry button.
	 * Stable `id` for the same tracking reason as {@link _retryingMessage}.
	 */
	private readonly _crashMessage = computed<BaseMessage[]>(() => {
		this._langChange(); // re-translate on language change
		return this.chatStore.pending_retry()
			? [new AIMessage({
					id: 'synthetic-agent-crashed',
					content: this.translate.instant('aiReport.lbl_something_went_wrong'),
					additional_kwargs: { is_agent_crashed: true },
				})]
			: [];
	});

	/**
	 * Conversation shown in the UI: LangGraph's messages followed by the durable crash bubble.
	 * The crash bubble persists across reload because it derives from the server-backed
	 * `pending_retry` flag. Auto-retries happen silently — no "Retrying…" line is shown.
	 */
	readonly messages = computed(() => [
		...(this.chatStore.messages() ?? []),
		...this._crashMessage(),
	]);
	readonly resultAssets = this.chatStore.resultAssets;

	readonly mainAssetId = computed(() => this.chatStore.summary()?.mainAssetId ?? null);

	/** AI-generated conversation title, used as the default reportName for one-click publish. */
	readonly mainAssetName = computed(() => this.chatStore.summary()?.name ?? null);

	private readonly _lastGeneratedAssetId = signal<string | null>(null);
	readonly lastGeneratedAssetId = this._lastGeneratedAssetId.asReadonly();

	readonly hoveredAsset = signal<Asset | null>(null);
	readonly insertAsset$ = new Subject<string>();

	insertAssetRef(assetId: string): void {
		this.insertAsset$.next(`#${assetId}`);
	}

	// ── Draft ────────────────────────────────────────────────────────────────────
	/**
	 * Current message draft being composed.
	 *
	 * Two-way bound in the template via `[(ngModel)]` → `[ngModel] + (ngModelChange)`.
	 * Cleared automatically after sending.
	 */
	readonly draft = signal('');

	/**
	 * True when the chat can start a new run (send or retry): credits loaded, not already
	 * streaming, and credits remaining. This is the shared gate; {@link canSend} adds the
	 * draft-content requirement on top for normal sends.
	 */
	readonly canInteract = computed(
		() => this.creditsService.balanceLoaded()
			&& !this.isStreaming()
			&& !this.creditsService.isEmpty()
	);

	/**
	 * True when the draft can be sent: {@link canInteract} plus non-whitespace draft content.
	 */
	readonly canSend = computed(
		() => this.canInteract() && this.draft().trim().length > 0
	);

	// ── Derived state ────────────────────────────────────────────────────────────
	/**
	 * True while loading initial chat state (no messages yet, stream is active).
	 *
	 * Used to show a loading spinner in the chat area on first message.
	 */
	readonly isChatLoading = computed(() => {
		const msgs = this.messages();
		return (!msgs || msgs.length === 0) && this.isStreaming();
	});

	/**
	 * True while a response is streaming (used for report assets loading state).
	 */
	readonly isLoading = computed(() => this.isStreaming() || this.chatStore.loading());

	/**
	 * Combined error state for the chat feature.
	 *
	 * Note: thread list errors are handled by the component via the resource.
	 */
	readonly error = computed(() => this.chatStore.error());

	constructor() {
		// Keep store in sync with the latest streamed graph state.
		this.chatStore.updateGraphState(
			this.graphState$.pipe(takeUntilDestroyed())
		);
	}

	/**
	 * Starts streaming a new assistant response.
	 *
	 * Concurrency: latest call wins; previous stream is canceled.
	 */
	sendMessage(content: string): void {
		const trimmed = content.trim();
		if (!trimmed) return;

		// Fresh turn: drop any stale crash state so the "Try again" bubble disappears.
		// The backend also clears pending_retry on run start.
		this.chatStore.setPendingRetry(false);
		this.chatStore.addHistoryMessage(trimmed);
		this._lastGeneratedAssetId.set(null);
		const s$ = this.imageGraphService.chat(trimmed, this.activeThreadId());
		this.currentStream$.next(s$);
	}

	/**
	 * Resumes the stream from the last LangGraph checkpoint without adding a new HumanMessage.
	 * Called when the user clicks the "Try again" button after an agent_crashed event.
	 */
	retry(): void {
		// Gate like a normal send (credits + not-already-streaming), minus the draft requirement,
		// and require an active thread to resume from — no-op otherwise.
		if (!this.canInteract() || !this.activeThreadId()) return;

		// Clear the crash state from the failed attempt before retrying. The backend also
		// clears pending_retry on run start; it is re-set only if this attempt also crashes.
		this.chatStore.setPendingRetry(false);
		this._lastGeneratedAssetId.set(null);
		const s$ = this.imageGraphService.chat('', this.activeThreadId(), true);
		this.currentStream$.next(s$);
	}

	/**
	 * Reads draft, sends the message, and clears the draft.
	 *
	 * Called by chat-input on button click or Ctrl+Enter.
	 * No-op if `canSend` is false or draft is empty.
	 */
	send(): void {
		if (!this.canSend()) return;
		const text = this.draft().trim();
		this.sendMessage(text);
		this.draft.set('');
	}

	/**
	 * Sets the draft value from a clicked suggestion.
	 *
	 * Called by chat-suggestions when user clicks a suggestion button.
	 * Allows the user to review/edit before sending.
	 */
	useSuggestion(s: string): void {
		this.draft.set(s);
	}

	/**
	 * Stops the current stream (if any).
	 */
	stop(): void {
		this.currentStream$.next(undefined);
		setTimeout(() => {
			this.chatStore.selectThread(this.activeThreadId);
		}, 500);
	}

	async newThread(): Promise<void> {
		this.currentStream$.next(undefined);
		this._lastGeneratedAssetId.set(null);
		await this.chatStore.selectThread('');
	}

	async selectThread(thread: Chat): Promise<void> {
		this.currentStream$.next(undefined);
		this._lastGeneratedAssetId.set(null);
		await this.chatStore.selectThread(thread.thread_id);
	}

	async deleteThread(thread_id: string): Promise<void> {
		await firstValueFrom(this.chatClient.deleteThread(thread_id));
		if (thread_id === this.chatStore.thread_id())
			this.chatStore.setThreadId('');
	}

	async deleteAllThreads(threadIds: string[]): Promise<void> {
		await Promise.all(
			threadIds.map((id) => firstValueFrom(this.chatClient.deleteThread(id)))
		);
		await this.chatStore.selectThread('');
	}

	/**
	 * Clears the local prompt history.
	 *
	 * Edge case: safe to call when history is already empty.
	 */
	clearHistory(): void {
		this.chatStore.clearHistory();
	}

	/**
	 * Removes a single prompt from local history.
	 *
	 * Edge case: if the message is not present, this is a no-op.
	 */
	deleteHistoryMessage(message: string): void {
		this.chatStore.deleteHistoryMessage(message);
	}
}
